import { z } from 'zod';

/** Frontend-only max; API does not enforce length (existing data may be longer). */
export const TASK_EDITOR_TITLE_MAX_LENGTH = 50;

const optionalNullableInt = (min: number, max: number) => z.preprocess(
  (value) => {
    if (value === '' || value === null || value === undefined) return null;
    const coerced = Number(value);
    return Number.isNaN(coerced) ? value : coerced;
  },
  z.number().int().min(min).max(max)
    .nullable(),
);

/**
 * Zod schema for the Task Editor form.
 * This schema defines the structure of the form state, which is a flattened
 * version of the API models for easier binding to UI components.
 */
export const taskFormSchema = z.object({
  id: z.string().optional(),
  title: z.string().min(1, 'Task must have a title').max(
    TASK_EDITOR_TITLE_MAX_LENGTH,
    `Title must be at most ${TASK_EDITOR_TITLE_MAX_LENGTH} characters`,
  ),
  // For oral exams the student-facing description is system-generated; this field
  // holds optional "additional comments", so an empty value is valid. The min-20
  // requirement therefore only applies to authored descriptions (all other types).
  description: z.string().default(''),
  type: z.enum(['essay', 'activity', 'event', 'multiple_choice', 'oral_exam']),
  sortIndex: z.number().default(0),
  level: z.number().min(1).max(3).default(1),

  // Flattened analysis (objectives)
  // Record<dimensionKey, demandsArray>
  analysis: z.record(z.array(z.string())).optional(),

  // Evaluation settings
  evaluation: z.object({
    /** AI tutor for students: auto = enabled, manual = off (matches student task UI). */
    mode: z.enum(['manual', 'auto']).default('manual'),
    gradingRelevant: z.boolean().or(z.null()).optional(),
    formalCriteriaSource: z.enum(['default', 'task_params', 'rubric']).optional(),
    formalCriteria: z.object({
      wordcount: z.preprocess((val) => Number(val), z.number()).default(0),
      wordcountTolerance: z.preprocess((val) => Number(val), z.number()).default(0),
      minInTextCitations: z.preprocess((val) => (val === '' || val === undefined ? undefined : Number(val)), z.number().min(0).optional()),
    }).strict().or(z.null()).optional(),
    completionPolicyOverride: z.enum(['inherit', 'free', 'gated']).or(z.null()).optional(),
    completionMinLevelsOverride: z.record(z.string()).or(z.null()).optional(),
    attachmentPolicy: z.enum(['none', 'images', 'extended']).or(z.null()).optional(),
  }).nullable().optional(),

  // MCQ configuration
  mcqConfig: z.object({
    questionsToShow: optionalNullableInt(1, 1000).optional(),
    passThresholdPercent: z.number().min(0).max(100).default(70),
    shuffleQuestions: z.boolean().default(true),
    shuffleOptions: z.boolean().default(true),
    showImmediateFeedback: z.boolean().default(true),
    timeLimitMinutes: z.number().nullable().default(null),
  }).nullable().optional(),

  // Rubric settings
  rubric: z.object({
    reasoningFlowchart: z.string().or(z.null()).optional(),
    dimensions: z.array(z.object({
      name: z.string(),
      max_pts: z.number(),
      criteria: z.array(z.object({
        name: z.string(),
        pts: z.number().or(z.string().transform((val) => Number(val) || 0)),
        descriptor: z.string(),
        dimension_critical: z.boolean().optional(),
      })),
      params: z.record(z.any()).optional(),
    })).optional(),
  }).nullable().optional(),

  // Event configuration
  eventConfig: z.object({
    eventKindId: z.preprocess(
      (value) => (value === null || value === '' ? undefined : value),
      z.string().optional(),
    ),
    requireDedicatedSlots: z.boolean().optional(),
    durationMinutesOverride: optionalNullableInt(15, 1000).optional(),
    capacityOverride: optionalNullableInt(1, 1000).optional(),
    requiresRecording: z.boolean().optional(),
    recordingConsentText: z.string().or(z.null()).optional(),
    unlockConditions: z.object({
      type: z.enum(['always', 'all_previous', 'specific_tasks']).optional(),
      taskIds: z.array(z.string()).optional(),
    }).strict().or(z.null()).optional(),
  }).nullable().optional(),
}).superRefine((data, ctx) => {
  // Authored descriptions must be substantial; oral exams use a system-generated
  // description and only an optional comment, so they are exempt.
  if (data.type !== 'oral_exam' && data.description.trim().length < 20) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Description must be longer than 20 characters',
      path: ['description'],
    });
  }
});

export type TaskFormData = z.infer<typeof taskFormSchema>;
